import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PortletService } from '../../features/billing/services/portlet.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class DownloadService {
  private readonly portletService = inject(PortletService);
  private readonly toastService = inject(ToastService);

  /**
   * Calls the billing service endpoint and triggers a file download via link click.
   * @param action  The billing service action (e.g. 'downloadInvoice', 'downloadDocument')
   * @param request The request payload for the billing service
   * @param fallbackFileName Default file name if the response doesn't include one
   * @param destroyRef DestroyRef from the calling component to auto-unsubscribe on destroy
   */
  download(
    action: string,
    request: Record<string, string>,
    fallbackFileName = 'document.pdf',
    destroyRef?: DestroyRef
  ): void {
    let source$ = this.portletService.orchestrateData(action, request);

    if (destroyRef) {
      source$ = source$.pipe(takeUntilDestroyed(destroyRef));
    }

    source$.subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          const downloadUrl = response?.link || response?.payload?.receiptLocation;
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = response.fileName || fallbackFileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          this.toastService.add({
            type: 'error',
            title: 'Error',
            message: 'An unexpected error occurred. Please try again.',
          });
        }
      },
      error: () => {
        this.toastService.add({
          type: 'error',
          title: 'Error',
          message: 'Failed to download document. Please try again.',
        });
      },
    });
  }
}
